import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Receipt, ArrowLeft, Calendar } from "lucide-react";
import { BillingRow } from "@/components/BillingHistory";

const yearOf = (iso) => {
  try { return new Date(iso).getFullYear(); } catch { return null; }
};

export default function BillingHistoryPage() {
  const [items, setItems] = useState(null);
  const [year, setYear] = useState("all");

  useEffect(() => {
    api.get("/payments/history", { params: { days: 0 } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  const years = useMemo(() => {
    if (!items) return [];
    return [...new Set(items.map((t) => yearOf(t.created_at)).filter(Boolean))].sort((a, b) => b - a);
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (year === "all") return items;
    return items.filter((t) => String(yearOf(t.created_at)) === String(year));
  }, [items, year]);

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="billing-history-page">
      <Link to="/dashboard/collector" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B64] hover:text-[#CC5A3A] mb-6" data-testid="billing-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
        <Receipt className="w-6 h-6 text-[#2D6A4F]" /> All receipts
      </h1>
      <p className="text-[#5C6B64] mt-2 mb-8">Your complete subscription billing history. Download a PDF receipt for any paid charge.</p>

      <div className="card-soft">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <span className="text-sm text-[#8A9A86]" data-testid="billing-page-count">{filtered.length} receipt{filtered.length === 1 ? "" : "s"}</span>
          {years.length > 0 && (
            <label className="inline-flex items-center gap-2 text-sm text-[#5C6B64]">
              <Calendar className="w-4 h-4 text-[#2D6A4F]" />
              <select
                data-testid="billing-year-filter"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="rounded-lg border border-[#1E3F33]/15 bg-white px-3 py-1.5 text-sm text-[#1E3F33] focus:outline-none focus:border-[#2D6A4F]"
              >
                <option value="all">All years</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
        </div>

        {items === null ? (
          <p className="text-sm text-[#8A9A86]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#8A9A86]" data-testid="billing-page-empty">
            {items.length === 0 ? "No subscription payments yet." : `No receipts for ${year}.`}
          </p>
        ) : (
          <ul className="divide-y divide-[#1E3F33]/8" data-testid="billing-page-list">
            {filtered.map((t) => <BillingRow key={t.id} t={t} />)}
          </ul>
        )}
      </div>
    </div>
  );
}
